import React, { memo, useEffect } from 'react';

import { Typography } from '@sravni/react-design-system';
import type { Tab, VITRINS_OFFERS_TYPE } from '@sravni/ugc/lib/specialOffers/types';

import type { CustomOneTabsComponentProps } from '../../helpers/getComponentByTabType';
import { getTabsInfo } from '../../helpers/getTabsInfo';
import {
  useActiveTabType,
  useAvailableTabs,
  useOffersByType,
  useRequestForOrganizationDone,
} from '../../store/hooks/offers';
import { BlockList } from '../BlockList';
import { Pixels } from '../Pixels';

import styles from './offers-blocks.module.scss';

export const OffersBlocks = memo(
  ({
    setShowPreloader,
    sendMessageToParentWindow,
    isMobile,
    maxDesktopVisibleItems,
    isWithLinkToAll,
    contentRef,
  }: CustomOneTabsComponentProps) => {
    const activeTabType = useActiveTabType() as Tab<VITRINS_OFFERS_TYPE>;
    const activeOffers = useOffersByType(activeTabType as Tab<VITRINS_OFFERS_TYPE>);
    const availableTabs = useAvailableTabs();
    const isRequestForOrganizationDone = useRequestForOrganizationDone();
    const loadedOffersCount = activeOffers?.length;

    useEffect(() => {
      if (!loadedOffersCount) {
        setShowPreloader.on();
      } else {
        setShowPreloader.off();
        sendMessageToParentWindow({ type: 'itemsCount', itemsCount: loadedOffersCount });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadedOffersCount]);

    useEffect(() => {
      if (!isRequestForOrganizationDone) return;

      const oldFormatTabs = availableTabs.map(({ type }) => type);
      sendMessageToParentWindow({ type: 'availableTabs', availableTabs: oldFormatTabs });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableTabs, isRequestForOrganizationDone]);

    if (!activeTabType) return null;

    const handleClickGoToAllLink = (url) => (e) => {
      e.preventDefault();
      sendMessageToParentWindow({
        type: 'generalLinkClick',
        url,
      });
    };

    const { generalLink, generalLinkLabel } = getTabsInfo(activeTabType);

    return (
      <>
        <Pixels />
        <BlockList
          sendMessageToParentWindow={sendMessageToParentWindow}
          isMobile={isMobile}
          maxDesktopVisibleItems={maxDesktopVisibleItems}
          items={activeOffers}
          activeTabType={activeTabType}
          contentRef={contentRef}
        />
        {isWithLinkToAll && (
          <Typography.Text size={14} className={isMobile ? 'h-mt-20' : 'h-mt-32'}>
            <Typography.Link
              color="blue"
              href={generalLink}
              className={styles.generalLink}
              onClick={handleClickGoToAllLink(generalLink)}
            >
              {generalLinkLabel}
            </Typography.Link>
          </Typography.Text>
        )}
      </>
    );
  },
);
